// src/view/debugDraw.ts — F3 debug çizimi (DESIGN §14, ARCHITECTURE §4.5): hurtbox kapsülleri (dik / yatan), AKTİF hitbox küreleri
// `world.systems.hitboxWorld` ile (sim ile aynı nokta — tek kaynak), kenar normali + edgeDist çubuğu, yürünebilir arena kenarı, uçan eşya
// yarıçapı. Tek LineSegments (dinamik tampon), tahsissiz (scratch + sabit tampon). Görünmezken hiçbir şey hesaplanmaz.
import * as THREE from 'three';
import type { HitboxLocator, SimWorld, Vec3 } from '../core/types.ts';
import { FState, MovePhase } from '../core/types.ts';
import { fwdX, fwdZ } from '../core/vec3.ts';
import { CAP_H, CAP_R, HURT_R, HURT_Y0, HURT_Y1, LYING_CAP_H, LYING_HURT_HALF, LYING_HURT_Y } from '../config/constants.ts';
import { KEY_DEBUG_DRAW } from '../config/controls.ts';
import { T } from '../config/tuning.ts';

const MAX_SEGMENTS = 6000;
const CIRCLE_N = 16;
const C_HURT = 0x4cff7a, C_CAP = 0x3a8cff, C_HIT = 0xff3b3b, C_EDGE_IN = 0x7fd8ff, C_EDGE_OUT = 0xff7f2a, C_ARENA = 0xffffff, C_ITEM = 0xffe066;

const _p: Vec3 = { x: 0, y: 0, z: 0 };
const _c = new THREE.Color();

export class DebugDraw {
  readonly lines: THREE.LineSegments;
  private readonly pos = new Float32Array(MAX_SEGMENTS * 6);
  private readonly col = new Float32Array(MAX_SEGMENTS * 6);
  private seg = 0;
  visible = false;
  /** hitboxWorld: WP9 `world.systems.hitboxWorld`u verir; null ise world.systems'tan okunur. */
  constructor(readonly scene: THREE.Object3D, private locator: HitboxLocator | null = null) {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('color', new THREE.BufferAttribute(this.col, 3).setUsage(THREE.DynamicDrawUsage));
    g.setDrawRange(0, 0);
    const m = new THREE.LineBasicMaterial({ vertexColors: true, depthTest: false, transparent: true, opacity: 0.9, toneMapped: false });
    this.lines = new THREE.LineSegments(g, m);
    this.lines.frustumCulled = false; this.lines.renderOrder = 100; this.lines.visible = false; this.lines.name = 'debugDraw';
    scene.add(this.lines);
  }

  /** F3 (config/controls KEY_DEBUG_DRAW) dinleyicisi; WP9 `bindKey(window)` çağırır. */
  bindKey(target: { addEventListener(type: 'keydown', fn: (e: KeyboardEvent) => void): void }): void {
    target.addEventListener('keydown', (e) => { if (e.code === KEY_DEBUG_DRAW) { e.preventDefault(); this.toggle(); } });
  }
  toggle(): void { this.setVisible(!this.visible); }
  setVisible(v: boolean): void { this.visible = v; this.lines.visible = v; if (!v) this.lines.geometry.setDrawRange(0, 0); }
  get segmentCount(): number { return this.seg; }

  /* ---------------------------------------------------------------- ilkel çizimler */

  private line(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, color: number): void {
    if (this.seg >= MAX_SEGMENTS) return;
    const o = this.seg * 6;
    this.pos[o] = x0; this.pos[o + 1] = y0; this.pos[o + 2] = z0; this.pos[o + 3] = x1; this.pos[o + 4] = y1; this.pos[o + 5] = z1;
    _c.setHex(color);
    this.col[o] = _c.r; this.col[o + 1] = _c.g; this.col[o + 2] = _c.b; this.col[o + 3] = _c.r; this.col[o + 4] = _c.g; this.col[o + 5] = _c.b;
    this.seg++;
  }
  /** Yatay çember (XZ düzlemi). */
  private circleXZ(cx: number, cy: number, cz: number, r: number, color: number): void {
    for (let i = 0; i < CIRCLE_N; i++) {
      const a0 = (i / CIRCLE_N) * Math.PI * 2, a1 = ((i + 1) / CIRCLE_N) * Math.PI * 2;
      this.line(cx + Math.cos(a0) * r, cy, cz + Math.sin(a0) * r, cx + Math.cos(a1) * r, cy, cz + Math.sin(a1) * r, color);
    }
  }
  /** Dik çember: eksen (ax, az) yatay yönü, düzlem o yön × Y. */
  private circleVertical(cx: number, cy: number, cz: number, r: number, ax: number, az: number, color: number): void {
    for (let i = 0; i < CIRCLE_N; i++) {
      const a0 = (i / CIRCLE_N) * Math.PI * 2, a1 = ((i + 1) / CIRCLE_N) * Math.PI * 2;
      this.line(cx + ax * Math.cos(a0) * r, cy + Math.sin(a0) * r, cz + az * Math.cos(a0) * r, cx + ax * Math.cos(a1) * r, cy + Math.sin(a1) * r, cz + az * Math.cos(a1) * r, color);
    }
  }
  private sphere(cx: number, cy: number, cz: number, r: number, color: number): void {
    this.circleXZ(cx, cy, cz, r, color); this.circleVertical(cx, cy, cz, r, 1, 0, color); this.circleVertical(cx, cy, cz, r, 0, 1, color);
  }
  /** Dik kapsül: taban y0, tepe y1, yarıçap r (uçlar yarım küre yerine iki çember + dört dikey). */
  private capsuleVertical(cx: number, cz: number, y0: number, y1: number, r: number, color: number): void {
    this.circleXZ(cx, y0 + r, cz, r, color); this.circleXZ(cx, y1 - r, cz, r, color);
    this.circleXZ(cx, (y0 + y1) / 2, cz, r, color);
    for (let i = 0; i < 4; i++) { const a = (i / 4) * Math.PI * 2; this.line(cx + Math.cos(a) * r, y0, cz + Math.sin(a) * r, cx + Math.cos(a) * r, y1, cz + Math.sin(a) * r, color); }
  }
  /** Yatan kapsül: merkez y, bakış yönünde ± half, yarıçap r. */
  private capsuleLying(cx: number, cy: number, cz: number, yaw: number, half: number, r: number, color: number): void {
    const fx = fwdX(yaw), fz = fwdZ(yaw);
    for (let i = -1; i <= 1; i++) this.circleVertical(cx + fx * half * i, cy, cz + fz * half * i, r, -fz, fx, color);
    const lx = fz, lz = -fx;   // yan
    this.line(cx - fx * half + lx * r, cy, cz - fz * half + lz * r, cx + fx * half + lx * r, cy, cz + fz * half + lz * r, color);
    this.line(cx - fx * half - lx * r, cy, cz - fz * half - lz * r, cx + fx * half - lx * r, cy, cz + fz * half - lz * r, color);
    this.line(cx - fx * half, cy + r, cz - fz * half, cx + fx * half, cy + r, cz + fz * half, color);
    this.line(cx - fx * half, cy - r, cz - fz * half, cx + fx * half, cy - r, cz + fz * half, color);
  }

  /* ---------------------------------------------------------------- kare */

  update(world: SimWorld): void {
    if (!this.visible) return;
    this.seg = 0;
    const locate = this.locator ?? world.systems.hitboxWorld;
    // Arena yürünebilir kenarları (üst yüzey)
    for (const p of world.arena.platforms) {
      if (!p.walkable) continue;
      const y = p.topY + 0.01;
      this.line(p.minX, y, p.minZ, p.maxX, y, p.minZ, C_ARENA); this.line(p.maxX, y, p.minZ, p.maxX, y, p.maxZ, C_ARENA);
      this.line(p.maxX, y, p.maxZ, p.minX, y, p.maxZ, C_ARENA); this.line(p.minX, y, p.maxZ, p.minX, y, p.minZ, C_ARENA);
    }
    for (const f of world.fighters) {
      if (f.state === FState.Eliminated) continue;
      const lying = f.state === FState.Lying || f.state === FState.Knockdown || f.state === FState.Roll || f.state === FState.Crawl || f.state === FState.Carried;
      // Fizik kapsülü (mavi) + hurtbox (yeşil)
      if (lying) {
        this.capsuleVertical(f.pos.x, f.pos.z, f.pos.y, f.pos.y + LYING_CAP_H, CAP_R, C_CAP);
        this.capsuleLying(f.pos.x, f.pos.y + LYING_HURT_Y, f.pos.z, f.yaw, LYING_HURT_HALF, HURT_R, C_HURT);
      } else {
        this.capsuleVertical(f.pos.x, f.pos.z, f.pos.y, f.pos.y + CAP_H, CAP_R, C_CAP);
        this.capsuleVertical(f.pos.x, f.pos.z, f.pos.y + HURT_Y0, f.pos.y + HURT_Y1, HURT_R, C_HURT);
      }
      // Bakış yönü
      this.line(f.pos.x, f.pos.y + 0.05, f.pos.z, f.pos.x + fwdX(f.yaw) * 0.8, f.pos.y + 0.05, f.pos.z + fwdZ(f.yaw) * 0.8, C_CAP);
      // Kenar normali ve edgeDist (içeride mavi, dışarıda turuncu); alarm mesafesinde kırmızı
      const ed = f.edgeDist;
      const ec = ed < 0 ? C_EDGE_OUT : ed < T.EDGE.alarmDist ? C_HIT : C_EDGE_IN;
      this.line(f.pos.x, f.pos.y + 0.02, f.pos.z, f.pos.x + f.edgeNx * ed, f.pos.y + 0.02, f.pos.z + f.edgeNz * ed, ec);
      // Aktif hitbox küreleri (systems.hitboxWorld; hamle tick'i penceresinde)
      if (f.state === FState.Attack && f.phase === MovePhase.Active) {
        const mv = world.moves[f.moveIdx];
        if (mv) for (const hb of mv.hitboxes) {
          if (f.moveTick < hb.from || f.moveTick > hb.to) continue;
          const r = locate(_p, world, f, hb);
          this.sphere(_p.x, _p.y, _p.z, r, C_HIT);
        }
      }
    }
    // Uçan eşya mermileri
    for (const it of world.items) {
      if (it.state !== 'flying') continue;
      const r = it.kind === '2h' ? T.ITEMS.throwRadius2H : T.ITEMS.throwRadius;
      this.sphere(it.pos.x, it.pos.y, it.pos.z, r, C_ITEM);
    }
    const g = this.lines.geometry;
    (g.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (g.getAttribute('color') as THREE.BufferAttribute).needsUpdate = true;
    g.setDrawRange(0, this.seg * 2);
  }

  dispose(): void { this.lines.removeFromParent(); this.lines.geometry.dispose(); (this.lines.material as THREE.Material).dispose(); }
}
